var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2023 Félix Robles <felix@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useContext, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { stringToHtml, isUndefined, normalizeWriteInText, translate, } from "@sequentech/ui-core";
import { Candidate } from "@sequentech/ui-essentials";
import Image from "mui-image";
import { resetBallotSelection, selectBallotSelectionQuestion, selectBallotSelectionVoteChoice, setBallotSelectionBlankVote, setBallotSelectionInvalidVote, setBallotSelectionVoteChoice, } from "../../store/ballotSelections/ballotSelectionsSlice";
import { checkAllowWriteIns, checkIsInvalidVote, checkIsWriteIn, getImageUrl, getLinkUrl, } from "../../services/ElectionConfigService";
import { useTranslation } from "react-i18next";
import { SettingsContext } from "../../providers/SettingsContextProvider";
import { provideBallotService } from "../../services/BallotService";
export var Answer = function (_a) {
    var answer = _a.answer, contestId = _a.contestId, ballotStyle = _a.ballotStyle, hasCategory = _a.hasCategory, isActive = _a.isActive, iconCheckboxPolicy = _a.iconCheckboxPolicy, isReview = _a.isReview, isInvalidVoteInput = _a.isInvalidVote, isExplicitBlankVote = _a.isExplicitBlankVote, isInvalidWriteIns = _a.isInvalidWriteIns, isRadioSelection = _a.isRadioSelection, contest = _a.contest, selectedChoicesSum = _a.selectedChoicesSum, setSelectedChoicesSum = _a.setSelectedChoicesSum, disableSelect = _a.disableSelect, explicitBlank = _a.explicitBlank, setExplicitBlank = _a.setExplicitBlank, setIsTouched = _a.setIsTouched;
    var selectionState = useAppSelector(selectBallotSelectionVoteChoice(ballotStyle.election_id, contestId, answer.id));
    var questionState = useAppSelector(selectBallotSelectionQuestion(ballotStyle.election_id, contestId));
    var question = ballotStyle.ballot_eml.contests.find(function (contest) { return contest.id === contestId; });
    var dispatch = useAppDispatch();
    var globalSettings = useContext(SettingsContext).globalSettings;
    var imageUrl = getImageUrl(answer);
    var infoUrl = getLinkUrl(answer);
    var i18n = useTranslation().i18n;
    var ballotService = provideBallotService();
    var isInvalidVote = useMemo(function () { return isInvalidVoteInput !== null && isInvalidVoteInput !== void 0 ? isInvalidVoteInput : checkIsInvalidVote(answer); }, [isInvalidVoteInput, answer]);
    var isChecked = function () {
        if (isInvalidVote) {
            return !isUndefined(questionState) && questionState.is_explicit_invalid;
        }
        else if (isExplicitBlankVote) {
            return (!isUndefined(questionState) &&
                !!ballotService.checkIsBlank(questionState) &&
                explicitBlank);
        }
        else {
            return !isUndefined(selectionState) && selectionState.selected > -1;
        }
    };
    var setInvalidVote = function (value) {
        dispatch(setBallotSelectionInvalidVote({
            ballotStyle: ballotStyle,
            contestId: contestId,
            isExplicitInvalid: value,
        }));
    };
    var setBlankVote = function () {
        setExplicitBlank(true);
        dispatch(setBallotSelectionBlankVote({
            ballotStyle: ballotStyle,
            contestId: contestId,
        }));
    };
    var setChecked = function (value) {
        if (!isActive || isReview) {
            return;
        }
        setIsTouched(true);
        if (isInvalidVote) {
            setInvalidVote(value);
            return;
        }
        if (isExplicitBlankVote) {
            if (value) {
                setBlankVote();
            }
            else {
                setExplicitBlank(false);
            }
            return;
        }
        else if (value && explicitBlank) {
            setExplicitBlank(false);
        }
        var cleanedText = (selectionState === null || selectionState === void 0 ? void 0 : selectionState.write_in_text) && normalizeWriteInText(selectionState === null || selectionState === void 0 ? void 0 : selectionState.write_in_text);
        if (isRadioSelection) {
            dispatch(resetBallotSelection({
                ballotStyle: ballotStyle,
                force: true,
                contestId: contest.id,
            }));
        }
        dispatch(setBallotSelectionVoteChoice({
            ballotStyle: ballotStyle,
            contestId: contestId,
            voteChoice: {
                id: answer.id,
                selected: value ? 0 : -1,
                write_in_text: cleanedText,
            },
        }));
    };
    var shouldDisable = disableSelect && (selectionState === null || selectionState === void 0 ? void 0 : selectionState.selected) === -1;
    var isWriteIn = checkIsWriteIn(answer);
    var allowWriteIns = question && checkAllowWriteIns(question);
    var setWriteInText = function (writeInText) {
        if (!isWriteIn || !allowWriteIns || !isActive || isReview) {
            return;
        }
        var cleanedText = normalizeWriteInText(writeInText);
        dispatch(setBallotSelectionVoteChoice({
            ballotStyle: ballotStyle,
            contestId: contestId,
            voteChoice: {
                id: answer.id,
                selected: isUndefined(selectionState) ? -1 : selectionState.selected,
                write_in_text: cleanedText,
            },
        }));
    };
    if (isReview && !isChecked()) {
        return null;
    }
    if (isReview && !!isExplicitBlankVote) {
        return null;
    }
    return (_jsx(Candidate, __assign({ title: translate(answer, "name", i18n.language), description: stringToHtml(translate(answer, "description", i18n.language) || ""), isActive: isActive, checked: isChecked(), setChecked: setChecked, url: infoUrl, hasCategory: hasCategory, isWriteIn: allowWriteIns && isWriteIn, writeInValue: selectionState === null || selectionState === void 0 ? void 0 : selectionState.write_in_text, setWriteInText: setWriteInText, isInvalidVote: isInvalidVote, isInvalidWriteIn: !!(selectionState === null || selectionState === void 0 ? void 0 : selectionState.write_in_text) && isInvalidWriteIns, shouldDisable: shouldDisable, iconCheckboxPolicy: iconCheckboxPolicy }, { children: imageUrl ? (_jsx(Image, { src: "".concat(globalSettings.PUBLIC_BUCKET_URL).concat(imageUrl), duration: 100 })) : null })));
};
